import { Plus, Trash2 } from "lucide-react";

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15";

const emptyFor = (f) => {
  if (f.type === "stringList") return [];
  return "";
};

const emptyItem = (f) => {
  const o = {};
  (f.fields || []).forEach((sf) => (o[sf.key] = emptyFor(sf)));
  return o;
};

// Leaf inputs only: text, textarea, stringList
const LeafInput = ({ field, value, onChange, testid }) => {
  if (field.type === "textarea") {
    return <textarea className={inputCls} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
  }
  if (field.type === "stringList") {
    return (
      <textarea
        className={inputCls}
        rows={4}
        value={Array.isArray(value) ? value.join("\n") : ""}
        onChange={(e) => onChange(e.target.value.split("\n").filter((l) => l.trim() !== ""))}
        placeholder="One item per line"
        data-testid={testid}
      />
    );
  }
  return <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
};

const ObjectListInput = ({ field, value, onChange, testid }) => {
  const items = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((item, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-blue">
              {field.itemLabel} {i + 1}
            </span>
            <button
              type="button"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="rounded-lg p-1.5 text-red-500 hover:bg-red-50"
              aria-label="Remove item"
              data-testid={`${testid}-remove-${i}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-3">
            {(field.fields || []).map((sf) => (
              <div key={sf.key}>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">{sf.label}</label>
                <LeafInput
                  field={sf}
                  value={item[sf.key]}
                  onChange={(v) => onChange(items.map((it, j) => (j === i ? { ...it, [sf.key]: v } : it)))}
                  testid={`${testid}-${i}-${sf.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, emptyItem(field)])}
        className="flex items-center gap-1.5 rounded-full border border-dashed border-brand-blue/40 px-4 py-2 text-xs font-bold text-brand-blue hover:bg-brand-blue/5"
        data-testid={`${testid}-add`}
      >
        <Plus className="h-3.5 w-3.5" /> Add {field.itemLabel || "item"}
      </button>
    </div>
  );
};

export const SchemaForm = ({ fields, value, onChange, sectionKey }) => (
  <div className="space-y-5">
    {fields.map((f, idx) => {
      const fieldValue = f.rootList ? value : value?.[f.key];
      const setField = (v) => (f.rootList ? onChange(v) : onChange({ ...value, [f.key]: v }));
      const testid = `editor-${sectionKey}-${f.key || "root"}`;
      return (
        <div key={f.key || idx}>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{f.label}</label>
          {f.type === "objectList" ? (
            <ObjectListInput field={f} value={fieldValue} onChange={setField} testid={testid} />
          ) : (
            <LeafInput field={f} value={fieldValue} onChange={setField} testid={testid} />
          )}
        </div>
      );
    })}
  </div>
);
